/**
 * isValidEmail — stricter email format check for auth forms.
 *
 * Tighter than native `type="email"` (rejects all-numeric hostnames like
 * `owner@123.com`) and enforces RFC 5321 length / dot / hyphen rules.
 */

/** Practical shape: local part + dot-separated labels + alphabetic TLD (2+). */
const EMAIL_REGEX =
  /^[a-zA-Z0-9!#$%&'*+/=?^_`{|}~.-]+@(?:[a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/;

export const INVALID_EMAIL_MESSAGE = "Please enter a valid email address.";

const MAX_EMAIL_LENGTH = 254;
const MAX_LOCAL_LENGTH = 64;
const MAX_DOMAIN_LABEL_LENGTH = 63;

/**
 * Returns `true` when `email` is a well-formed address the app accepts.
 */
function isValidEmail(email: string): boolean {
  const trimmed = email.trim();
  if (!trimmed || trimmed.length > MAX_EMAIL_LENGTH) return false;
  if (!EMAIL_REGEX.test(trimmed)) return false;

  const [local, domain] = trimmed.split("@") as [string, string];

  // RFC 5321 local-part constraints.
  if (local.length > MAX_LOCAL_LENGTH) return false;
  if (local.startsWith(".") || local.endsWith(".") || local.includes(".."))
    return false;

  const labels = domain.split(".");
  for (const label of labels) {
    if (label.length > MAX_DOMAIN_LABEL_LENGTH) return false;
    if (label.startsWith("-") || label.endsWith("-")) return false;
  }

  // Hostname (label before TLD) must contain a letter — rejects `owner@123.com`.
  const hostname = labels[labels.length - 2];
  if (!/[a-zA-Z]/.test(hostname)) return false;

  return true;
}

export default isValidEmail;
